import { useState } from 'react';

import {
  ComboBox,
  DateRangePicker,
  DropDownButton,
  NumericTextBox,
  TextBox,
  type DropDownItem,
} from '@/components/ui';

import {
  filterModeIcons,
  isFilterMode,
  NUMBER_FILTER_ITEMS,
  TEXT_FILTER_ITEMS,
  toFilterOperator,
  type FilterMode,
} from './filterOperators';
import type { FilterValue, GridColumn, GridFilterActions, GridPredicate } from './types';

/** `BoolCombo` aus `IwmGridFilterRow.razor`. */
const BOOL_ITEMS = ['', 'Yes', 'No'];

/** Markiert eine Verneinung; `buildWhereQuery` löst das Präfix wieder auf (REQ-03). */
const NOT_PREFIX = '$$NOT$$ ';

/**
 * Klasse jeder Eingabe der Filterzeile — auch des Datumsbereichs und der Ja/Nein-Auswahl,
 * die ohne Operator-Knopf unmittelbar in der Filterzelle sitzen. Die Aufteilung der Zelle
 * steht vollständig in `styles/components/_grid-filter.scss` (REQ-16): nur als Klasse — nicht
 * als Inline-Stil — greifen die Regeln, mit denen Eingabefeld und Bereichsfilter in schmalen
 * Spalten schrumpfen bzw. umbrechen statt über die Zelle hinauszulaufen.
 */
const VALUE_CLASS = 'grid-filter-value';

/** Untere und obere Grenze eines Bereichsfilters; leere Grenzen entfallen. */
function boundPredicates(field: string, from: FilterValue, to: FilterValue): GridPredicate[] {
  const predicates: GridPredicate[] = [];
  if (from !== null) predicates.push({ field, operator: 'greaterthanorequal', value: from });
  if (to !== null) predicates.push({ field, operator: 'lessthanorequal', value: to });
  return predicates;
}

export interface GridFilterRowProps<T> {
  column: GridColumn<T>;
  handle: GridFilterActions;
}

/** Port von `IwmGridFilterRow.razor` — Filterzelle je Datentyp der Spalte. */
export function GridFilterRow<T>(props: GridFilterRowProps<T>) {
  const { column, handle } = props;
  const field = column.field;
  const numeric = column.valueType === 'number' || column.valueType === 'integer';

  const [mode, setMode] = useState<FilterMode>(numeric ? 'Equal' : 'Contains');
  const [text, setText] = useState('');
  const [single, setSingle] = useState<number | null>(null);
  const [from, setFrom] = useState<number | null>(null);
  const [to, setTo] = useState<number | null>(null);
  const [range, setRange] = useState<{ startDate: Date | null; endDate: Date | null }>({
    startDate: null,
    endDate: null,
  });
  const [choice, setChoice] = useState('');

  const operatorButton = (items: readonly DropDownItem[]) => (
    <div className="grid-filter-operator">
      <DropDownButton
        cssClass="grid-filter-dropdown"
        iconClass={`e-icons ${filterModeIcons[mode]}`}
        items={[...items]}
        onItemSelected={(item) => {
          if (isFilterMode(item.id)) setMode(item.id);
        }}
      />
    </div>
  );

  const applyText = (value: string) => {
    setText(value);
    const operator = toFilterOperator(mode);
    if (value === '' || operator === null) {
      handle.clearFiltering(field);
      return;
    }
    handle.filterByColumn(field, operator, mode === 'NotContains' ? NOT_PREFIX + value : value);
  };

  const applyNumber = (value: number | null) => {
    setSingle(value);
    const operator = toFilterOperator(mode);
    if (value === null || operator === null) {
      handle.clearFiltering(field);
      return;
    }
    handle.filterByColumn(field, operator, value);
  };

  const applyBetween = (start: number | null, end: number | null) => {
    handle.setColumnWhere(field, { predicates: boundPredicates(field, start, end) });
  };

  const applyRange = (start: Date | null, end: Date | null) => {
    if (start === null && end === null) {
      handle.setColumnWhere(field, null);
      return;
    }
    handle.setColumnWhere(field, { predicates: boundPredicates(field, start, end) });
  };

  const applyChoice = (value: string) => {
    setChoice(value);
    if (value === '') {
      handle.clearFiltering(field);
      return;
    }
    handle.filterByColumn(field, 'equal', value === 'Yes');
  };

  if (column.valueType === 'date') {
    return (
      <DateRangePicker
        className={VALUE_CLASS}
        placeholder="Filter"
        showClearButton
        startDate={range.startDate}
        endDate={range.endDate}
        onChange={(next) => {
          setRange(next);
          applyRange(next.startDate, next.endDate);
        }}
      />
    );
  }

  if (column.valueType === 'boolean') {
    return (
      <ComboBox className={VALUE_CLASS} value={choice} items={BOOL_ITEMS} onChange={applyChoice} />
    );
  }

  if (numeric) {
    const format = column.valueType === 'integer' ? 'd' : 'N2';
    return (
      <div className="grid-filter">
        {operatorButton(NUMBER_FILTER_ITEMS)}
        {mode === 'Between' ? (
          <div className={`${VALUE_CLASS} grid-filter-range`}>
            <NumericTextBox
              value={from}
              placeholder="Von"
              format={format}
              min={0}
              step={1}
              onChange={(value) => {
                setFrom(value);
                applyBetween(value, to);
              }}
            />
            <NumericTextBox
              value={to}
              placeholder="Bis"
              format={format}
              min={0}
              step={1}
              onChange={(value) => {
                setTo(value);
                applyBetween(from, value);
              }}
            />
          </div>
        ) : (
          <NumericTextBox
            className={VALUE_CLASS}
            value={single}
            placeholder="Filter"
            format={format}
            min={0}
            step={1}
            onChange={applyNumber}
          />
        )}
      </div>
    );
  }

  return (
    <div className="grid-filter">
      {operatorButton(TEXT_FILTER_ITEMS)}
      <TextBox className={VALUE_CLASS} value={text} placeholder="Filter" onChange={applyText} />
    </div>
  );
}

export default GridFilterRow;
